import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Search, Shield, Users } from 'lucide-react';

import { request } from '@/api/client';
import { PageHeader } from '@/components/AppShell';
import { DataTable, type Column } from '@/components/DataTable';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  TableSkeleton,
  TextInput,
} from '@/components/ui';
import { count } from '@/lib/format';
import { useDebounced, useResource } from '@/lib/hooks';
import { useDataScope, type DataPurpose } from '@/lib/dataPurpose';
import { PurposeBadge, PurposeEditor } from '@/components/DataPurpose';

// Rows 14/15 and the mail of 14.08: a team is held by one owner, and when that
// owner is unreachable the dashboard is the way to appoint the next one. This
// page exists so the operator can see which teams are headless and fix them
// without a database session.

type Team = {
  id: string;
  data_purpose: DataPurpose;
  data_purpose_override: DataPurpose | null;
  name: string;
  club_name: string | null;
  club_id?: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  owner_email: string | null;
  member_count: number;
};

type Staff = {
  user_id: string;
  role: string;
  display_name: string;
  email: string;
};

export function TeamsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { scope } = useDataScope();
  const [purposeEditing, setPurposeEditing] = useState<Team | null>(null);
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const search = (params.get('search') ?? '').slice(0, 120);
  const headlessOnly = params.get('headless') === '1';
  const source = `${location.pathname}${location.search}`;
  function teamUrl(team: Team): string {
    const query = new URLSearchParams({ tab: 'athletes', team_id: team.id, from: source });
    return `/clubs/${encodeURIComponent(team.club_id ?? '')}?${query}`;
  }
  const [editing, setEditing] = useState<Team | null>(null);
  const debouncedSearch = useDebounced(search);

  const { data, error, loading, reload } = useResource<Team[]>(
    '/api/v1/admin/teams',
    { search: debouncedSearch || undefined, data_purpose: scope },
    [debouncedSearch, scope],
  );

  const language = i18n.language;
  const rows = (data ?? []).filter((team) => !headlessOnly || !team.owner_user_id);
  const headlessCount = (data ?? []).filter((team) => !team.owner_user_id).length;

  const columns: Column<Team>[] = [
    {
      key: 'name',
      header: t('teams.name'),
      render: (team) => (
        <div className="min-w-0">
          {team.club_id ? <Link to={teamUrl(team)} className="font-medium text-brand hover:underline">{team.name}</Link> : <div className="font-medium text-ink">{team.name}</div>}
          <div className="truncate text-2xs text-ink-subtle">{team.club_name ?? '—'}</div>
          <div className="mt-1"><PurposeBadge purpose={team.data_purpose} /></div>
        </div>
      ),
    },
    {
      key: 'owner',
      header: t('teams.owner'),
      render: (team) =>
        team.owner_user_id ? (
          <div className="min-w-0">
            <div className="truncate text-ink">{team.owner_name}</div>
            <div className="truncate text-2xs text-ink-subtle">{team.owner_email}</div>
          </div>
        ) : (
          <Badge tone="caution">
            <AlertTriangle aria-hidden className="mr-1 inline h-3 w-3" />
            {t('teams.noOwner')}
          </Badge>
        ),
    },
    {
      key: 'members',
      header: t('teams.members'),
      align: 'right',
      render: (team) => count(team.member_count, language),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (team) => (
        <div className="flex justify-end gap-2">
        {team.club_id ? <Link className="inline-flex min-h-9 items-center rounded-lg px-3 text-sm text-brand hover:underline" to={teamUrl(team)}>{t('clubs.showAthletes')}</Link> : null}
        <Button onClick={() => setPurposeEditing(team)}>{t('dataPurpose.edit')}</Button>
        <Button
          icon={<Shield className="h-3.5 w-3.5" />}
          disabled={team.member_count === 0}
          onClick={() => setEditing(team)}
        >
          {team.owner_user_id ? t('teams.changeOwner') : t('teams.setOwner')}
        </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('teams.title')} subtitle={t('teams.subtitle')} />

      <div className="card">
        <div className="flex flex-wrap items-center gap-3 border-b border-line p-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
            />
            <TextInput
              type="search"
              aria-label={t('common.search')}
              placeholder={t('teams.searchPlaceholder')}
              className="pl-9"
              value={search}
              onChange={(event) => setParams((current) => { const next = new URLSearchParams(current); event.target.value ? next.set('search', event.target.value) : next.delete('search'); return next; }, { replace: true })}
            />
          </div>
          <label className="flex min-h-[2.25rem] items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line text-brand"
              checked={headlessOnly}
              onChange={(event) => setParams((current) => { const next = new URLSearchParams(current); event.target.checked ? next.set('headless', '1') : next.delete('headless'); return next; }, { replace: true })}
            />
            {t('teams.headlessOnly', { count: headlessCount })}
          </label>
        </div>

        {error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : loading && !data ? (
          <TableSkeleton columns={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Users className="h-8 w-8" />}
            title={t('teams.emptyTitle')}
            body={t(headlessOnly ? 'teams.emptyHeadless' : 'teams.emptyBody')}
          />
        ) : (
          <DataTable
            caption={t('teams.title')}
            columns={columns}
            rows={rows}
            rowKey={(team) => team.id}
          />
        )}
      </div>

      {editing ? (
        <SetOwner
          team={editing}
          onClose={() => setEditing(null)}
          onDone={(name) => {
            setEditing(null);
            toast.success(t('teams.ownerSet', { team: editing.name, name }));
            reload();
          }}
        />
      ) : null}
      {purposeEditing ? <PurposeEditor entityType="teams" entityId={purposeEditing.id}
        name={purposeEditing.name} purpose={purposeEditing.data_purpose} override={purposeEditing.data_purpose_override}
        onClose={() => setPurposeEditing(null)} onSaved={() => { setPurposeEditing(null); reload(); }} /> : null}
    </>
  );
}

function SetOwner({
  team,
  onClose,
  onDone,
}: {
  team: Team;
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [staff, setStaff] = useState<Staff[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    // Ownership eligibility is independent of the reporting scope.
    request<Staff[]>(`/api/v1/admin/teams/${team.id}/staff`, { signal: controller.signal })
      .then((rows) => {
        // The current owner is not a candidate for their own team.
        const candidates = rows.filter((row) => row.user_id !== team.owner_user_id);
        setStaff(candidates);
        if (candidates.length === 1) setChosen(candidates[0].user_id);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [team.id, team.owner_user_id]);

  async function save() {
    const target = staff?.find((row) => row.user_id === chosen);
    if (!target) return;
    setBusy(true);
    try {
      await request(`/api/v1/admin/teams/${team.id}/owner`, {
        method: 'PUT',
        body: { user_id: target.user_id },
      });
      onDone(target.display_name);
    } catch {
      toast.failure(t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('teams.setOwnerTitle', { team: team.name })}
      description={t('teams.setOwnerHint')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} disabled={!chosen} onClick={save}>
            {t('teams.setOwner')}
          </Button>
        </>
      }
    >
      {failed ? (
        <p className="text-sm text-ink-muted">{t('common.loadFailed')}</p>
      ) : staff === null ? (
        <p className="text-sm text-ink-subtle">{t('common.loading')}</p>
      ) : staff.length === 0 ? (
        // Nobody on the squad can hold it: the team has no coach-account staff
        // besides (possibly) the current owner. The way out is a staff invite
        // code redeemed by a new coach, then this dialog again.
        <p className="text-sm text-ink-muted">{t('teams.noCandidates')}</p>
      ) : (
        <Field label={t('teams.newOwner')}>
          {(id, describedBy) => (
            <select
              id={id}
              aria-describedby={describedBy}
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink"
              value={chosen}
              onChange={(event) => setChosen(event.target.value)}
            >
              <option value="">{t('teams.pickOne')}</option>
              {staff.map((row) => (
                <option key={row.user_id} value={row.user_id}>
                  {row.display_name} · {t(`teams.roles.${row.role}`, row.role)} · {row.email}
                </option>
              ))}
            </select>
          )}
        </Field>
      )}
    </Modal>
  );
}
